import { defineStore } from "pinia"
import { computed, ref, watch } from "vue"
import axios, { AxiosError } from "axios"
import { useToastStore } from "./toast-store"
import { useGeneralStore } from "./general-store"
import { useRouter } from "vue-router"

export const useNotificationStore = defineStore("notifications", () => {
  const toastStore = useToastStore()
  const generalStore = useGeneralStore()
  const router = useRouter()

  // Holds the UUIDs of all notifications that we have already shown natively.
  const osNotifications = new Set<string>()

  const notifications = ref<NotificationJSON[]>([])
  const hasUnreadNotifications = computed(() => {
    return notifications.value.filter(n => !n.read).length > 0
  })
  const notificationPermission = ref<"granted"|"default"|"denied"|undefined>(Notification?.permission)
  const notificationAPIAvailable = Notification !== undefined

  watch(notifications, (value) => {
    const onlyUnread = value.filter(n => !n.read)

    for (const notification of onlyUnread) {
      if (osNotifications.has(notification.uuid)) {
        // This notification has already been shown natively.
        continue
      }
      // Always add new notifications, even if showing doesn't work to avoid an
      // avalanche of notifications IF the user enables native notifications.
      osNotifications.add(notification.uuid)

      const osNotification = new Notification(generalStore.appName, {
        body: notification.message
      })

      osNotification.addEventListener("click", event => doAction(notification))
    }
  })

  async function requestNativeNotificationPermission () {
    // According to MDN, the Notification API is undefined on Safari iOS, unless
    // the app has been installed as a PWA to the homescreen.
    if (!notificationAPIAvailable) {
      return toastStore.toast("Cannot enable OS-level notifications: The necessary API is unavailable.", "danger")
    }

    if (Notification.permission !== "default") {
      return // Respect the user choice: Do not ask if the user already made a decision.
    }

    const result = await Notification.requestPermission()
    notificationPermission.value = result

    if (result === "granted") {
      toastStore.toast("OS-level notifications enabled.", "success")
    } else {
      toastStore.toast("OS-level notifications are not enabled. Visit the app to check your notifications", "danger")
    }
  }

  async function fetchNotifications () {
    const response = await axios.post("/api/user/get-notifications")
    const notes: NotificationJSON[] = response.data.notifications
    // Sort the notifications by time descending, so that the newest notification
    // is always on top.
    const coll = Intl.Collator(["en-US"])
    notes.sort((a, b) => -coll.compare(a.time, b.time))

    notifications.value = notes
  }

  // Fetch every minute
  setInterval(() => {
    fetchNotifications().catch(err => {
      if (err instanceof AxiosError && err.response?.status === 401) {
        return // Expected if the user is not logged in
      }

      console.error(err)
    })
  }, 1000 * 60)

  function markAsRead (notification: NotificationJSON) {
    axios.post("/api/user/mark-notification-read", { uuid: notification.uuid })
      .then(() => {
        notification.read = true
      })
      .catch(err => console.error(err))
  }

  function remove (notification: NotificationJSON) {
    axios.post("/api/user/remove-notification", { uuid: notification.uuid })
      .then(() => {
        fetchNotifications().catch(err => console.error(err))
      })
      .catch(err => {
        console.error(err)
        toastStore.toast("Could not remove notification", "danger")
      })
  }

  function doAction (notification: NotificationJSON) {
    if (notification.actionUrl !== undefined) {
      const url = new URL(notification.actionUrl)
      axios.post("/api/user/mark-notification-read", { uuid: notification.uuid })
        .then(() => {
          notification.read = true
          if (url.origin === window.location.origin) {
            // Using the router to navigate to a same-origin action URL saves
            // some flickering, since the page won't go through a full reload.
            router.push({ path: url.pathname }).catch(err => {
              console.error(err)
              toastStore.toast("Could not follow link", "danger")
            })
          } else {
            window.location.href = url.href
          }
        })
        .catch(err => {
          console.error(err)
          toastStore.toast("Could not mark notification as read", "danger")
        })
    }

    // Initial fetch & setup of interval
    // TODO: Move to websocket system
    setInterval(() => {
      fetchNotifications().catch(err => console.error(err))
    }, 1000 * 60) // Check for new notifications every minute
  }

  return { notifications, hasUnreadNotifications, notificationPermission, notificationAPIAvailable, markAsRead, remove, doAction, requestNativeNotificationPermission, fetchNotifications }
})
